/**
 * Shared session-refresh helper.
 *
 * The httpOnly `fias_session` cookie has a 60m Cognito-bound TTL while
 * the refresh cookie lives 30 days. Three paths need to ask the
 * backend to mint a fresh session cookie from the refresh token:
 *
 *   1. The 401 interceptor in `lib/api.ts` (a request just failed and
 *      we want to retry transparently).
 *   2. The proactive timer in `AuthContext` (refresh before expiry so
 *      the user never sees a 401 mid-action).
 *   3. The auth-api client's public `refreshSession` for explicit
 *      callers.
 *
 * Concentrating the logic here means:
 *
 *   - **Concurrent 401s coalesce.** When several in-flight API calls
 *     all fail with 401 around the same instant the session expired,
 *     they share a single in-flight refresh promise instead of
 *     stampeding the backend with N parallel `/auth/refresh` calls.
 *   - **CSRF stays consistent.** The new csrf token from the refresh
 *     response is captured in one place, so any caller's subsequent
 *     state-changing request will pass the double-submit check.
 *   - **One dispatch point** for `fias:session-expired`, the window
 *     event the SessionExpiredListener modal listens to.
 */

import { API_BASE } from './api';
import { setCsrfToken, attachCsrfHeader } from './csrf';
import { sessionCredentials } from './embed-document';

let inFlightRefresh: Promise<boolean> | null = null;

/**
 * Tab-lifetime flag for "has this tab ever held an authenticated session?".
 * Gates `notifySessionExpired` so a 401 on a public route (e.g., an anon
 * viewer of a public arche triggering an auth-required side call) does NOT
 * pop the "session expired" modal — there was no session to expire.
 *
 * Set true by auth-api on successful login / /auth/me, and by a successful
 * refresh here. Cleared by auth-api on logout. Intentionally never re-cleared
 * by a failed refresh: that's the case the modal exists for.
 */
let sessionEverEstablished = false;

export function markSessionEstablished(): void {
  sessionEverEstablished = true;
}

export function clearSessionEstablished(): void {
  sessionEverEstablished = false;
}

/**
 * Persistent, reload-durable "this browser has held a session" hint.
 *
 * Distinct from `sessionEverEstablished` above, which is in-memory and
 * therefore always false on a cold page load. That flag is deliberately
 * NEVER cleared by a failed refresh (it gates the session-expired modal);
 * this hint clears on a definitive refresh rejection. Different lifecycles
 * ⇒ separate flags.
 *
 * Scope: this is a RENDERING hint, not an authorization one. It decides whether
 * `Home` shows the boot splash instead of flashing the marketing site at a
 * visitor who is about to be redirected into the app. It also used to gate the
 * cold-boot refresh-and-retry in auth-api's `getCurrentUser`, and must not
 * again: localStorage is script-writable storage, which Safari's ITP deletes
 * after 7 days without a first-party visit, while the httpOnly `fias_refresh`
 * cookie it was standing in for lives a full 30 days. Any gate built on this
 * key expires before the credential it guards and logs iOS users out early.
 * Whether a session can be recovered is the server's answer to
 * POST /auth/refresh, nothing else.
 *
 * Why localStorage and not the `fias_csrf` cookie: the app deploys
 * cross-origin (frontend on `platform.fias.io`, API on
 * `api.platform.fias.io`). The auth cookies are host-only for the API
 * origin, so the frontend cannot read them via `document.cookie` on a
 * cold load. localStorage is first-party to the frontend origin and
 * survives reloads, so it is a signal the first render can observe.
 *
 * The value is a bare `'1'` — NOT a token or credential. It carries zero
 * security value (an attacker learning it gains only "this browser
 * probably has a session", which is not exploitable), so this does not
 * reintroduce the localStorage-token XSS risk called out in auth-api.ts.
 */
const SESSION_HINT_KEY = 'fias_session_hint';

export function markSessionHint(): void {
  try {
    localStorage.setItem(SESSION_HINT_KEY, '1');
  } catch {
    // SSR / private mode / storage disabled — the hint is a best-effort
    // optimization, so silently degrade to "no hint".
  }
}

export function clearSessionHint(): void {
  try {
    localStorage.removeItem(SESSION_HINT_KEY);
  } catch {
    // See markSessionHint — storage may be unavailable.
  }
}

export function hasSessionHint(): boolean {
  try {
    return localStorage.getItem(SESSION_HINT_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * Attempts to refresh the session cookie via POST /v1/auth/refresh.
 * Concurrent callers receive the same in-flight promise. Returns true
 * if the backend issued a fresh session cookie, false if the refresh
 * token was rejected or the network call failed.
 *
 * Never throws — callers branch on the boolean result.
 */
export async function refreshAuthSession(): Promise<boolean> {
  if (inFlightRefresh) return inFlightRefresh;

  inFlightRefresh = (async () => {
    try {
      const headers: Record<string, string> = {};
      // /auth/refresh server-side bypasses CSRF (the user has no
      // session cookie at this point — the refresh cookie itself is
      // the credential). Echoing a known token costs nothing and keeps
      // the request shape identical to other state-changing calls.
      attachCsrfHeader('POST', headers);

      const response = await fetch(`${API_BASE}/auth/refresh`, {
        method: 'POST',
        headers,
        credentials: sessionCredentials(),
      });

      if (!response.ok) {
        // Refresh-token expired or revoked — clear the stale csrf so
        // subsequent requests don't echo a value that no longer pairs
        // with any session. Also clear the persistent boot hint so the
        // next cold load doesn't waste a round-trip trying to recover a
        // session that's definitively gone.
        setCsrfToken(null);
        clearSessionHint();
        return false;
      }

      const body = (await response.json().catch(() => ({}))) as { csrfToken?: string };
      if (typeof body.csrfToken === 'string') {
        setCsrfToken(body.csrfToken);
      }
      sessionEverEstablished = true;
      markSessionHint();
      return true;
    } catch {
      // Network error: don't notify session-expired (it might come
      // back). Caller can decide how to surface a transient failure.
      return false;
    } finally {
      inFlightRefresh = null;
    }
  })();

  return inFlightRefresh;
}

/**
 * Fires `fias:session-expired` so the SessionExpiredListener modal
 * (mounted once at the App root) can prompt the user to sign in again
 * with their location preserved for return.
 *
 * No-op when this tab has never held an authenticated session. The 401
 * interceptor in api.ts calls us on any 401 — including ones an anonymous
 * viewer can hit on public-arche routes that incidentally call an
 * auth-required side endpoint — and "your session has expired" is wrong
 * messaging for a user who was never logged in.
 *
 * Idempotent — the listener guards against repeated dispatches while
 * the modal is already open.
 */
export function notifySessionExpired(): void {
  if (typeof window === 'undefined') return;
  if (!sessionEverEstablished) return;
  window.dispatchEvent(new CustomEvent('fias:session-expired'));
}
